import { useCallback } from "react";
import { ecosternApi } from "api/ecosternApi";
import { IClientEvent, IClientEventRequest } from "models/api/IClientEvent";
import { store } from "store";
import { useAppSelector } from "store/hooks";
import { selectCurrentClientId, selectIsUserEcostern, selectUserConfiguration } from "store/slices/userSlice";

const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUED_EVENTS = 50;

// Queued per client, as the selected client may change before the queue is flushed
const queue = new Map<number, IClientEventRequest[]>();
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

const flush = async () => {
    if (flushTimeout) clearTimeout(flushTimeout);
    flushTimeout = null;
    if (queue.size === 0) return;
    if (!navigator.onLine) {
        scheduleFlush();
        return;
    }

    const requests = Array.from(queue.entries()).map(([clientId, events]) =>
        store.dispatch(ecosternApi.endpoints.addClientEvents.initiate({ clientId, events }, { track: false }))
    );
    queue.clear();
    await Promise.all(requests);
};

// Events need the user's token, so they must be sent before logging out
export const flushClientEvents = flush;

const scheduleFlush = () => {
    if (!flushTimeout) {
        flushTimeout = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
};

document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
});

export const useClientEvents = () => {
    const clientId = useAppSelector(selectCurrentClientId);
    const isUserEcostern = useAppSelector(selectIsUserEcostern);
    const configuration = useAppSelector(selectUserConfiguration);
    const isEnabled = !!clientId && !isUserEcostern && configuration?.clientEventsEnabled !== false;

    const addEvent = useCallback(
        (event: IClientEvent) => {
            if (!clientId || !isEnabled) return;
            const events = [...(queue.get(clientId) ?? []), { ...event, occurredAt: new Date().toISOString() }];
            queue.set(clientId, events);
            if (events.length >= MAX_QUEUED_EVENTS) {
                flush();
            } else {
                scheduleFlush();
            }
        },
        [clientId, isEnabled]
    );

    return { addEvent };
};
